import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Send, Headphones, Star, Shield, MessageSquare,
  CreditCard, HelpCircle, Settings, AlertTriangle, Loader2
} from 'lucide-react';
import api from '@/utils/api';
import { useLang } from '@/utils/i18n';
import { MobileBottomNav } from '@/components/MobileNav';

const CATEGORIES = [
  { id: 'general', label: 'Allgemein', labelEn: 'General', icon: HelpCircle },
  { id: 'technical', label: 'Technisches Problem', labelEn: 'Technical Issue', icon: Settings },
  { id: 'payment', label: 'Zahlung & Abo', labelEn: 'Payment & Plan', icon: CreditCard },
  { id: 'tips', label: 'Tipps & Analyse', labelEn: 'Tips & Analysis', icon: MessageSquare },
  { id: 'account', label: 'Konto & Daten', labelEn: 'Account & Data', icon: Shield },
  { id: 'report', label: 'Problem melden', labelEn: 'Report Issue', icon: AlertTriangle },
];

const SupportPage = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('general');
  const [sessionId, setSessionId] = useState('');
  const [isPriority, setIsPriority] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const chatEndRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/support/history');
      if (data.tickets && data.tickets.length > 0) {
        const msgs = [];
        data.tickets.forEach((t) => {
          msgs.push({ role: 'user', text: t.user_message, category: t.category, time: t.created_at });
          msgs.push({ role: 'assistant', text: t.ai_response, priority: t.is_priority, time: t.created_at });
        });
        setMessages(msgs);
        setSessionId(data.tickets[data.tickets.length - 1].session_id || '');
        setIsPriority(data.tickets[data.tickets.length - 1].is_priority || false);
      }
    } catch {
      // No history
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: userMsg, category }]);
    setLoading(true);

    try {
      const { data } = await api.post('/support/ask', {
        message: userMsg,
        category,
        session_id: sessionId,
      });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: data.response, priority: data.is_priority },
      ]);
      setSessionId(data.session_id);
      setIsPriority(data.is_priority);
      setShowRating(true);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: 'Fehler beim Senden. Bitte versuche es erneut.', error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const submitRating = async () => {
    if (!sessionId || rating === 0) return;
    try {
      await api.post(`/support/rate/${sessionId}`, { rating });
      setShowRating(false);
    } catch {
      // Silently fail
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] text-gray-200 flex flex-col pb-14 md:pb-0">
      {/* Header */}
      <div className="bg-[#1E1E1E] border-b border-white/10 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="support-back-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Headphones className="w-5 h-5 text-[#00BFA6]" />
            <div>
              <h1 data-testid="support-title" className="text-lg font-bold text-white">
                {lang === 'de' ? 'Support' : 'Support'}
              </h1>
              <p className="text-xs text-gray-400">
                {isPriority ? (
                  <span className="text-[#7B1FA2] font-semibold">Priority Support</span>
                ) : (
                  <span>{lang === 'de' ? 'KI-gestützter Support' : 'AI-powered Support'}</span>
                )}
              </p>
            </div>
          </div>
          {isPriority && (
            <div data-testid="priority-badge" className="bg-[#7B1FA2]/20 border border-[#7B1FA2]/50 text-[#7B1FA2] text-xs font-bold px-3 py-1 rounded-full">
              PRIORITY
            </div>
          )}
        </div>
      </div>

      {/* Category selector */}
      <div className="bg-[#1A1A1A] border-b border-white/5 px-4 py-3 overflow-x-auto">
        <div className="max-w-4xl mx-auto flex gap-2">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                data-testid={`support-category-${cat.id}`}
                onClick={() => setCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  category === cat.id
                    ? 'bg-[#00BFA6]/20 text-[#00BFA6] border border-[#00BFA6]/40'
                    : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {lang === 'de' ? cat.label : cat.labelEn}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div data-testid="support-welcome" className="text-center py-16 space-y-4">
              <div className="w-16 h-16 mx-auto bg-[#00BFA6]/10 rounded-full flex items-center justify-center">
                <Headphones className="w-8 h-8 text-[#00BFA6]" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {lang === 'de' ? 'Wie können wir helfen?' : 'How can we help?'}
              </h2>
              <p className="text-gray-400 text-sm max-w-md mx-auto">
                {lang === 'de'
                  ? 'Wähle eine Kategorie oben und stelle deine Frage. Unser KI-Support antwortet sofort.'
                  : 'Select a category above and ask your question. Our AI support responds instantly.'}
              </p>
              {/* Quick actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto mt-8">
                {[
                  { text: lang === 'de' ? 'Wie kündige ich mein Abo?' : 'How do I cancel?', cat: 'payment' },
                  { text: lang === 'de' ? 'Tipps werden nicht geladen' : 'Tips not loading', cat: 'technical' },
                  { text: lang === 'de' ? 'Wie funktioniert die KI?' : 'How does the AI work?', cat: 'general' },
                  { text: lang === 'de' ? 'Konto löschen' : 'Delete account', cat: 'account' },
                ].map((qa, i) => (
                  <button
                    key={i}
                    data-testid={`support-quick-${i}`}
                    onClick={() => {
                      setCategory(qa.cat);
                      setInput(qa.text);
                    }}
                    className="bg-[#1E1E1E] border border-white/10 rounded-xl px-4 py-3 text-left text-sm text-gray-300 hover:border-[#00BFA6]/40 hover:text-white transition-all"
                  >
                    {qa.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              data-testid={`support-msg-${idx}`}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#00BFA6]/20 text-white rounded-br-md'
                    : msg.error
                    ? 'bg-red-900/20 border border-red-500/30 text-red-300 rounded-bl-md'
                    : msg.priority
                    ? 'bg-[#7B1FA2]/10 border border-[#7B1FA2]/30 text-gray-200 rounded-bl-md'
                    : 'bg-[#1E1E1E] border border-white/10 text-gray-200 rounded-bl-md'
                }`}
              >
                {msg.role === 'assistant' && msg.priority && (
                  <div className="text-[#7B1FA2] text-xs font-bold mb-1 flex items-center gap-1">
                    <Star className="w-3 h-3" /> Priority Support
                  </div>
                )}
                <p className="whitespace-pre-wrap">{msg.text}</p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#1E1E1E] border border-white/10 rounded-2xl rounded-bl-md px-4 py-3">
                <Loader2 className="w-4 h-4 animate-spin text-[#00BFA6]" />
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Rating */}
      {showRating && !loading && messages.length > 0 && (
        <div data-testid="support-rating" className="bg-[#1A1A1A] border-t border-white/5 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-3">
            <span className="text-xs text-gray-400">
              {lang === 'de' ? 'War das hilfreich?' : 'Was this helpful?'}
            </span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  data-testid={`support-star-${s}`}
                  onClick={() => { setRating(s); submitRating(); }}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <Star className={`w-4 h-4 ${s <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="bg-[#1E1E1E] border-t border-white/10 px-4 py-4">
        <div className="max-w-4xl mx-auto flex gap-3">
          <input
            data-testid="support-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={lang === 'de' ? 'Deine Frage an den Support...' : 'Your question...'}
            className="flex-1 bg-[#121212] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#00BFA6]/50 text-sm"
            disabled={loading}
          />
          <Button
            data-testid="support-send-btn"
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold px-5 py-3 rounded-xl disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default SupportPage;
